// Single source of truth for lot size: `raw_board_lot_qty` from the scrip info API.
// A missing, zero, negative or non-numeric value is treated as 1.
export const getLotSize = (scripInfo) => {
  const lotSize = Number(scripInfo?.raw_board_lot_qty);
  return lotSize > 0 ? lotSize : 1;
};

// Lot size as a positive finite number, or null when it is missing or invalid.
export const parseLotSize = (lotsize) => {
  const lotSize = Number(lotsize);
  return Number.isFinite(lotSize) && lotSize > 0 ? lotSize : null;
};

// ---- Lots <-> quantity conversion ----------------------------------------------------------
// Quantity is always a whole number. Lots may have decimals: just enough to show a single unit
// of quantity (lotDecimals). The maths runs on whole numbers of the smallest lots step
// (10^-decimals) instead of floats, so results are exact (0.07 lots x 100 = 7, never 8).

const MAX_LOT_DECIMALS = 15;

// Fewest decimals that still show 1 quantity: the smallest d with 10^d >= lotSize
// (lot size 1 -> 0, 10 -> 1, 75 or 100 -> 2, 200 or 1000 -> 3, 100000 -> 5).
export const lotDecimals = (lotSize) => {
  let decimals = 0;
  while (decimals < MAX_LOT_DECIMALS && 10 ** decimals < lotSize) decimals += 1;
  return decimals;
};

const isEmptyValue = (value) => value === undefined || value === null || String(value).trim() === "";

// Lots text -> lots counted in steps of 10^-decimals, read digit by digit (never via parseFloat).
const toScaledLots = (text, decimals) => {
  const [wholePart = "", fractionPart = ""] = String(text).split(".");
  const fractionDigits = (fractionPart + "0".repeat(decimals)).slice(0, decimals);
  return Number(wholePart || "0") * 10 ** decimals + Number(fractionDigits || "0");
};

// Lots counted in steps of 10^-decimals -> display text without trailing zeros ("1.5", "2").
const formatScaledLots = (scaledLots, decimals) => {
  const scale = 10 ** decimals;
  const whole = Math.floor(scaledLots / scale);
  if (decimals === 0) return String(whole);
  const fraction = String(scaledLots % scale).padStart(decimals, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : String(whole);
};

// Text allowed in a Lots input while typing: digits, at most one "." and at most `decimals`
// decimals. In-progress values such as "", "1." and ".5" are allowed.
export const isValidLotsText = (text, decimals) =>
  decimals === 0
    ? /^\d*$/.test(text)
    : new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`).test(text);

// Lots shown for a quantity: quantity / lotSize cut off (not rounded) to lotDecimals.
// (15, 10) -> "1.5", (100, 75) -> "1.33", (199, 200) -> "0.995"; "" while quantity is empty.
export const quantityToLotsText = (quantity, lotSize) => {
  if (isEmptyValue(quantity)) return "";
  const qty = parseInt(quantity, 10);
  if (!Number.isFinite(qty) || qty < 0) return "";
  const decimals = lotDecimals(lotSize);
  return formatScaledLots(Math.floor((qty * 10 ** decimals) / lotSize), decimals);
};

// Quantity for typed lots: lots x lotSize rounded UP to a whole number.
// ("0.61", 75) -> 46, ("0.6", 10) -> 6, ("1.5", 75) -> 113. Returns null if the result is too
// large to be represented exactly.
export const lotsTextToQuantity = (text, lotSize) => {
  const decimals = lotDecimals(lotSize);
  const scale = 10 ** decimals;
  const product = toScaledLots(text, decimals) * lotSize;
  if (!Number.isFinite(product) || product > Number.MAX_SAFE_INTEGER) return null;
  return Math.floor(product / scale) + (product % scale > 0 ? 1 : 0);
};

// Quantity after an arrow-key step in a Lots input: up goes to the next whole lot, down to the
// previous one (1.5 -> 2 / 1, 2 -> 3 / 1), never below 0 lots.
export const quantityAfterLotsStep = (text, lotSize, direction) => {
  const decimals = lotDecimals(lotSize);
  const scale = 10 ** decimals;
  const scaledLots = toScaledLots(text || "0", decimals);
  const nextLots = direction > 0
    ? Math.floor(scaledLots / scale) + 1
    : Math.ceil(scaledLots / scale) - 1;
  return Math.max(0, nextLots) * lotSize;
};
